import {
  createStyles,
  Navbar,
  getStylesRef,
  rem,
  NavLink,
  Text,
  Badge,
  Group,
} from "@mantine/core";
import {
  IconLogout,
  IconSettingsAutomation,
  IconDashboard,
  IconMessageCircle,
  IconBriefcase,
  IconUsers,
  IconPencil,
  IconCreditCard,
  IconListCheck,
  IconLayoutBoard,
  IconCalendarDue,
  IconFileTypography,
  IconWorld,
  IconSpeakerphone,
  IconClock,
  IconHistory,
  IconFiles,
  IconRocket,
  IconBulb,
  IconDeviceMobile,
} from "@tabler/icons";
import { UserButton } from "./UserButton";
import { useLocation } from "react-router-dom";
import { auth } from "../firebase-config";
import { signOut } from "@firebase/auth";
import { useNavigate } from "react-router-dom";
import { lighten, darken } from "../utils/color";
import { clearSignInMethod } from "./AuthMethod";
import { disablePushNotifications } from "../services/PushNotifications";
import { hasExtraRole } from "../services/UserRoles";
import { openWebsiteQrPrompt } from "./WebsiteQrPrompt";

const useStyles = createStyles((theme, { accent }) => ({
  header: {
    paddingBottom: theme.spacing.md,
    marginBottom: `calc(${theme.spacing.md} * 1.5)`,
    borderBottom: `${rem(1)} solid ${theme.colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[2]}`,
  },

  footer: {
    paddingTop: theme.spacing.md,
    marginTop: theme.spacing.md,
    borderTop: `${rem(1)} solid ${theme.colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[2]}`,
  },

  link: {
    ...theme.fn.focusStyles(),
    display: "flex",
    alignItems: "center",
    textDecoration: "none",
    fontSize: theme.fontSizes.md,
    color: theme.colorScheme === "dark" ? theme.white : theme.colors.gray[7],
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    borderRadius: theme.radius.sm,
    fontWeight: 600,

    "&:hover": {
      // In dark mode, tinting towards white (as in light mode) would paint a bright patch on a
      // dark navbar - so this mixes towards black instead, matching the same fix used for the
      // "today" column in Booker.
      backgroundColor: theme.colorScheme === "dark" ? darken(accent, 0.75) : lighten(accent, 0.85),
      color: theme.colorScheme === "dark" ? theme.white : accent,
      boxShadow: theme.shadows.md,
      [`& .${getStylesRef("icon")}`]: {
        color: theme.colorScheme === "dark" ? theme.white : accent,
      },
    },
  },

  linkIcon: {
    ref: getStylesRef("icon"),
    color: theme.colorScheme === "dark" ? theme.colors.dark[2] : theme.colors.gray[6],
    marginRight: theme.spacing.sm,
  },

  // Feedback/Roadmap gets its own quiet resting tint - a low-opacity teal rather than the bright
  // saturated accent - so it draws a little attention among the plainer links above it without
  // looking like it's already selected. Declared before linkActive below so, once it IS the
  // active route, linkActive's own background still wins on the CSS cascade tie-break.
  feedbackLink: {
    backgroundColor: theme.fn.rgba(theme.colors.teal[theme.colorScheme === "dark" ? 7 : 4], 0.18),
    "&:hover": {
      backgroundColor: theme.fn.rgba(theme.colors.teal[theme.colorScheme === "dark" ? 6 : 4], 0.3),
    },
  },

  linkActive: {
    "&, &:hover": {
      boxShadow: theme.shadows.md,
      backgroundColor: accent,
      color: theme.white,
      fontWeight: 900,
      [`& .${getStylesRef("icon")}`]: {
        color: theme.white,
      },
    },
  },

  // Logout is destructive/exits the account, so it's tinted red rather than sharing the same
  // hover treatment as an ordinary navigation link - and sits with a bit of extra breathing room
  // below the avatar so it doesn't read as just another row in the same group.
  logoutLink: {
    ...theme.fn.focusStyles(),
    display: "flex",
    alignItems: "center",
    textDecoration: "none",
    fontSize: theme.fontSizes.md,
    color: theme.colorScheme === "dark" ? theme.colors.red[4] : theme.colors.red[7],
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    borderRadius: theme.radius.sm,
    fontWeight: 600,
    marginTop: theme.spacing.xs,

    "&:hover": {
      backgroundColor: theme.colorScheme === "dark" ? theme.colors.red[9] : theme.colors.red[0],
    },
  },
}));

export function AppNavBar({ currentUser, userClaims, companyData, closeMenu, userToken, companyId }) {
  const { classes, cx } = useStyles({ accent: companyData["accent"] || "#228be6" });
  const navigate = useNavigate();
  // Light/dark/device mode is now set from Profile > Appearance instead of a toggle here - see
  // src/components/ColorSchemeMode.js.
  const location = useLocation();

  // Which link is "active" is derived straight from the router's own current location on every
  // render, rather than a separate bit of state that only this component's own click handlers
  // ever updated - that meant it could quietly drift out of sync with the real URL whenever a
  // navigation happened some other way (typing a URL directly, browser back/forward, another
  // page calling navigate() itself, e.g. the Harvest OAuth callback screen), leaving a link's
  // click handler thinking it was already "there" and silently doing nothing.
  const active = location.pathname;

  // Optional-chained all the way through - a company doc that simply doesn't have one of these
  // top-level fields yet (e.g. a feature never configured for that company) used to throw here
  // on every single page render, since this component sits in the persistent nav and mounts
  // regardless of route. With no error boundary anywhere in the app, that crash showed up as a
  // blank white page rather than just that one nav link being hidden.
  const isAutomationEnabled = companyData["azure"]?.["automation_enabled"] ?? false;
  const isTestrailEnabled = companyData["testrail"]?.["enabled"] ?? false;
  const isSMSEnabled = companyData["sms"]?.["enabled"] ?? false;
  const isBoardsEnabled = companyData["jira"]?.["boards_enabled"] ?? false;
  const isHarvestForecastEnabled =
    (companyData["harvest_forecast"]?.["harvest_enabled"] ?? false) ||
    (companyData["harvest_forecast"]?.["forecast_enabled"] ?? false);

  const dataClient = [
    // No Booker link in this list (client accounts don't see it) - kept first here so it still
    // sits in the same relative spot Booker would otherwise anchor it to.
    { link: "/harcast", label: "Harcast", icon: IconClock, visible: isHarvestForecastEnabled },
    // Client accounts can still see the Documents link - the page itself only ever shows a
    // folder/file if this specific client has been explicitly granted access to it (see Edit
    // Company > Documents > Manage Access), so there's no harm showing the link even when a
    // client currently has nothing shared with them yet.
    { link: "/documents", label: "Documents", icon: IconFiles, visible: true, beta: true },
    { link: "/automations", label: "Automations", icon: IconSettingsAutomation, visible: isAutomationEnabled },
    { link: "/jira", label: "Jira Boards", icon: IconLayoutBoard, visible: isBoardsEnabled },
    { link: "/testrail", label: "TestRail", icon: IconListCheck, visible: isTestrailEnabled },
    // Runway's client-facing counterpart - only ever shows whichever build a developer/admin/
    // super has pinned this client to, per project (see MyBuilds.js). Shown unconditionally, same
    // reasoning as Documents above - the page itself just shows an empty state if nothing's
    // assigned yet.
    { link: "/my-builds", label: "My Builds", icon: IconRocket, visible: true },
    // Lets a client find their own device's UDID to send in for Ad-Hoc distribution - visible
    // unconditionally, same reasoning as My Builds just above.
    { link: "/udid", label: "Get Device UDID", icon: IconDeviceMobile, visible: true },
  ];
  const dataFeatures = [
    { link: "/", label: "Dashboard", icon: IconDashboard },
    { link: "/booker", label: "Booker", icon: IconCalendarDue, visible: true },
    { link: "/harcast", label: "Harcast", icon: IconClock, visible: isHarvestForecastEnabled },
    { link: "/documents", label: "Documents", icon: IconFiles, visible: true, beta: true },
    { link: "/automations", label: "Automations", icon: IconSettingsAutomation, visible: isAutomationEnabled },
    { link: "/jira", label: "Jira Boards", icon: IconLayoutBoard, visible: isBoardsEnabled },
    { link: "/testrail", label: "TestRail", icon: IconListCheck, visible: isTestrailEnabled },
    { link: "/sms-messages", label: "SMS Messaging", icon: IconMessageCircle, visible: isSMSEnabled },
    {
      link: "/runway",
      label: "Runway",
      icon: IconRocket,
      beta: true,
      // App-build distribution - admin/super and the "developer" sub-role only (see the original
      // request). A plain employee doesn't get this link at all.
      visible:
        userClaims["access"] === "admin" || userClaims["access"] === "super" || hasExtraRole(userClaims, "developer"),
    },
    { link: "/udid", label: "Get Device UDID", icon: IconDeviceMobile, visible: true },
  ];
  // Opens a URL saved under Edit Company > Certifications in a new tab (not an in-app iframe) -
  // hidden until an admin has set one. http(s) only, so a stored value can never be a javascript:
  // URL.
  const isWebUrl = (value) => /^https?:\/\//i.test(value ?? "");
  const dataDurian = [
    {
      link: companyData["embryo"]?.["url"],
      target: "_blank",
      promptTitle: "Durian website",
      showQr: companyData["embryo"]?.["show_qr"] !== false,
      label: "Website",
      icon: IconWorld,
      visible: isWebUrl(companyData["embryo"]?.["url"]),
    },
    { link: "/verification/terms-and-conditions", label: "T&C Verification", icon: IconFileTypography, visible: true },
    { link: "/verification/card-artwork", label: "Card Artwork Verification", icon: IconCreditCard, visible: true },
  ];
  const dataErdnuss = [
    {
      link: companyData["erdnuss"]?.["url"],
      target: "_blank",
      promptTitle: "Erdnuss website",
      showQr: companyData["erdnuss"]?.["show_qr"] !== false,
      label: "Website",
      icon: IconWorld,
      visible: isWebUrl(companyData["erdnuss"]?.["url"]),
    },
    {
      link: "/erdnuss/verification/terms-and-conditions",
      label: "T&C Verification",
      icon: IconFileTypography,
      visible: true,
    },
    {
      link: "/erdnuss/verification/card-artwork",
      label: "Card Artwork Verification",
      icon: IconCreditCard,
      visible: true,
    },
  ];
  const dataCompany = [
    {
      link: "/posts",
      label: "Posts",
      icon: IconSpeakerphone,
      // Managing posts is admin/super only - everyone already sees the posts themselves on the
      // Dashboard, so regular employees don't need a nav entry for creating them.
      visible: userClaims["access"] === "admin" || userClaims["access"] === "super",
    },
    { link: "/clients", label: "Clients", icon: IconBriefcase },
    { link: "/employees", label: "Employees", icon: IconUsers },
    {
      link: "/company/edit",
      label: "Edit Company",
      icon: IconPencil,
      visible: userClaims["access"] === "admin" || userClaims["access"] === "super",
    },
    {
      link: "/audit-log",
      label: "Activity Log",
      icon: IconHistory,
      // Hidden per request - left in place (rather than deleted) so it's a one-line change to
      // bring back if needed, instead of having to rebuild the entry from scratch.
      visible: false,
    },
  ];

  // A "Beta" tag next to a nav item's label - Documents and Runway both carry this right now (see
  // the beta: true flag on their entries above), rather than a hardcoded label string, so it's a
  // one-line change to add/remove from any other feature later.
  const renderNavLabel = (item) =>
    item.beta ? (
      <Group spacing={6} noWrap>
        <Text size="sm">{item.label}</Text>
        <Badge size="xs" variant="light" color="pink">
          Beta
        </Badge>
      </Group>
    ) : (
      item.label
    );

  const clientLinks = dataClient
    .filter((i) => i.visible ?? true)
    .map((item) => (
      <NavLink
        className={cx(classes.link, { [classes.linkActive]: active === item.link })}
        key={item.label}
        label={renderNavLabel(item)}
        icon={<item.icon size="1.15rem" stroke={1.5} />}
        onClick={(event) => {
          event.preventDefault();
          if (item.link !== active) {
            navigate(item.link);
            closeMenu(true);
          }
        }}
      />
    ));

  // Durian and Erdnuss sections build their links the same way: an item with a `target` is an
  // external website, which (unless an admin switched the QR code off for it under Edit Company >
  // Certifications) first offers a QR code to scan, or a button to continue, rather than opening
  // straight away - anything else is an in-app route.
  const renderSectionLinks = (data) =>
    data
      .filter((i) => i.visible ?? true)
      .map((item) => (
        <NavLink
          className={cx(classes.link, { [classes.linkActive]: active === item.link })}
          style={item.link === active ? { pointerEvents: "none" } : {}}
          key={item.label}
          label={item.label}
          target={item.target}
          onClick={(event) => {
            event.preventDefault();
            if (item.target) {
              if (item.showQr === false) {
                window.open(item.link, item.target, "noopener,noreferrer");
              } else {
                openWebsiteQrPrompt({ url: item.link, title: item.promptTitle ?? item.label });
              }
            } else if (item.link !== active) {
              navigate(item.link);
              closeMenu(true);
            }
          }}
          icon={<item.icon size="1.15rem" stroke={1.5} />}
        />
      ));
  const linksDurian = renderSectionLinks(dataDurian);
  const linksErdnuss = renderSectionLinks(dataErdnuss);

  const featureLinks = dataFeatures
    .filter((i) => i.visible ?? true)
    .map((item) => (
      <NavLink
        className={cx(classes.link, { [classes.linkActive]: active === item.link })}
        style={item.link === active ? { pointerEvents: "none" } : {}}
        key={item.label}
        target={item.target ?? "_self"}
        label={renderNavLabel(item)}
        icon={<item.icon size="1.15rem" stroke={1.5} />}
        childrenOffset={32}
        onClick={(event) => {
          event.preventDefault();
          if (item.link !== active) {
            navigate(item.link);
            closeMenu(true);
          }
        }}
      ></NavLink>
    ));

  const companyLinks = dataCompany
    .filter((i) => i.visible ?? true)
    .map((item) => (
      <NavLink
        className={cx(classes.link, { [classes.linkActive]: active === item.link })}
        key={item.label}
        label={item.label}
        icon={<item.icon size="1.15rem" stroke={1.5} />}
        onClick={(event) => {
          event.preventDefault();
          if (item.link !== active) {
            navigate(item.link);
            closeMenu(true);
          }
        }}
      />
    ));

  return (
    <Navbar width={{ sm: 300 }} p="md">
      {/* The scrollable middle section - grows to fill whatever space the fixed footer below
          (theme toggle / user / logout) doesn't need, and scrolls internally once its own
          content (nav links, plus Durian/Company sub-sections) is taller than that. minHeight: 0
          overrides a flex column child's default "never shrink below content size" behaviour,
          which is what was otherwise letting this section's content just overflow past the
          Navbar's own bottom edge - unreachable on mobile (where the Navbar itself becomes the
          slide-in drawer) rather than actually scrolling. */}
      <Navbar.Section grow sx={{ overflowY: "auto", minHeight: 0 }}>
        {userClaims["access"] === "client" ? (
          clientLinks
        ) : (
          <>
            {featureLinks}
            {hasExtraRole(userClaims, "durian") && (
              <Navbar.Section className={classes.footer}>
                <Text fw="bold" fz="xs" c="dimmed">
                  Certification - Durian
                </Text>{" "}
                {linksDurian}
              </Navbar.Section>
            )}
            {hasExtraRole(userClaims, "erdnuss") && (
              <Navbar.Section className={classes.footer}>
                <Text fw="bold" fz="xs" c="dimmed">
                  Certification - Erdnuss
                </Text>{" "}
                {linksErdnuss}
              </Navbar.Section>
            )}
            <Navbar.Section className={classes.footer}>
              <Text fw="bold" fz="xs" c="dimmed">
                Company
              </Text>
              {companyLinks}
            </Navbar.Section>
          </>
        )}
      </Navbar.Section>

      <Navbar.Section className={classes.footer}>
        {/* Feedback/Roadmap - "put the button above the users profile link in the nav drawer"
            from the original request - an internal Jira-style board, employees only. */}
        {userClaims["access"] !== "client" && (
          <NavLink
            className={cx(classes.link, classes.feedbackLink, { [classes.linkActive]: active === "/feedback" })}
            label="Feedback/Roadmap"
            icon={<IconBulb size="1.15rem" stroke={1.5} />}
            onClick={(event) => {
              event.preventDefault();
              if (active !== "/feedback") {
                navigate("/feedback");
                closeMenu(true);
              }
            }}
          />
        )}

        <UserButton
          image={companyData["avatar"]}
          name={currentUser?.displayName}
          currentUser={currentUser}
          userClaims={userClaims}
          userToken={userToken}
          companyId={companyId}
          companyData={companyData}
          isHarvestForecastEnabled={isHarvestForecastEnabled}
        />

        <a
          href="/"
          className={classes.logoutLink}
          onClick={(e) => {
            // A real <a href="/"> - without this, the browser's own native navigation to "/"
            // fires immediately on click and almost always wins the race against the async
            // signOut() below, hard-reloading the page while Firebase Auth is still signed in
            // (the signOut() call gets abandoned mid-flight). That reload then lands right back
            // on the Dashboard, looking exactly like "logout doesn't work". preventDefault() here
            // hands navigation entirely to the react-router navigate("/") call once signOut()
            // has actually finished.
            e.preventDefault();
            // Unsubscribes this device's push token first - best-effort (disablePushNotifications
            // never throws out to here), so a shared/public computer stops receiving this
            // account's post notifications once they've signed out of it. No localStorage.clear()
            // here otherwise - it used to wipe every device-level preference on logout (dark/light
            // mode, Booker's section order/collapse state, and a couple of other small UI
            // toggles), none of which are session-specific or sensitive - they're meant to stick
            // to this device regardless of who's currently signed in.
            disablePushNotifications({ userIdToken: userToken, companyId }).finally(() =>
              signOut(auth).then(() => {
                clearSignInMethod();
                navigate("/");
                closeMenu(true);
              })
            );
          }}
        >
          <IconLogout stroke={1.5} size="1.15rem" style={{ marginRight: "0.6rem" }} />
          <span>Logout</span>
        </a>
      </Navbar.Section>
    </Navbar>
  );
}
